/**
 * Client model translations
 *
 * Translation keys follow the pattern: models.client.{category}.{key}
 * Categories: fields, tabs, blocks, badges, actions, filters, options
 *
 * Note: vue-i18n resolves dots as nested paths, so nested field keys
 * like "client_data.tier" must be structured as nested objects.
 */
export default {
  en: {
    // Model names
    singular: "Client",
    plural: "Clients",

    // Tabs
    tabs: {
      general: "General",
      client: "Client Details",
      billing: "Billing",
      tasks: "Tasks",
      key_management: "Key Management",
      keys: "Keys",
      notices: "Notices",
      submissions: "Filled forms",
      equipment_control_reports: "Equipment control reports"
    },

    // Blocks
    blocks: {
      identity: "Identity",
      contact: "Contact",
      address: "Address",
      contact_methods: "Contact Methods",
      classification: "Classification",
      financial: "Financial",
      preferences: "Preferences",
      transaction_settings: "Transaction Settings",
      client_info: "Client Information"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Identity fields
      code: "Code",
      name: "Name",
      legal_name: "Legal Name",
      type: "Type",
      status: "Status",
      label: "Label",
      website: "Website",
      notes: "Notes",
      primaryAddress: {
        street_number: "Street Number",
        address: "Address",
        city: "City",
        zip: "Postal Code",
        state: "State / Province",
        country: "Country",
        full_address: "Full Address"
      },
      created_at: "Created At",
      updated_at: "Updated At",

      // Client-specific data (JSONB)
      client_data: {
        tier: "Tier",
        client_status: "Client Status",
        source: "Source",
        credit_limit_cents: "Credit Limit",
        payment_terms_days: "Payment Terms (Days)",
        tax_exempt: "Tax Exempt",
        tax_exempt_certificate: "Tax Exempt Certificate",
        account_manager_id: "Account Manager",
        acquired_at: "Acquired At",
        churned_at: "Churned At",
        churn_reason: "Churn Reason",
        suspended_at: "Suspended At",
        suspension_reason: "Suspension Reason",
        preferred_contact_method: "Preferred Contact Method",
        billing_frequency: "Billing Frequency",
        invoice_delivery: "Invoice Delivery",
        service_priority: "Service Priority"
      },

      // Transaction settings (JSONB)
      transaction_settings: {
        currency: "Currency",
        language: "Language",
        default_discount: "Default Discount"
      }
    },

    // Badges (for badge columns and select displays)
    badges: {
      // Status
      active: "Active",
      inactive: "Inactive",

      // Client Status
      prospect: "Prospect",
      suspended: "Suspended",
      churned: "Churned",

      // Tier
      standard: "Standard",
      silver: "Silver",
      gold: "Gold",
      platinum: "Platinum",
      enterprise: "Enterprise",

      // Type
      company: "Company",
      individual: "Individual",
      government: "Government",
      nonprofit: "Non-Profit",
      other: "Other",

      // Boolean
      true: "Yes",
      false: "No"
    },

    // Filters
    filters: {
      status: "Status",
      "client_data.tier": "Tier",
      "client_data.client_status": "Client Status",
      type: "Type"
    },

    // Options (for select/toggleGroup fields)
    options: {
      type: {
        company: "Company",
        individual: "Individual",
        government: "Government",
        nonprofit: "Non-Profit",
        other: "Other"
      },
      client_data: {
        tier: {
          standard: "Standard",
          silver: "Silver",
          gold: "Gold",
          platinum: "Platinum",
          enterprise: "Enterprise"
        },
        client_status: {
          prospect: "Prospect",
          active: "Active",
          suspended: "Suspended",
          churned: "Churned"
        },
        preferred_contact_method: {
          email: "Email",
          phone: "Phone",
          sms: "SMS"
        },
        billing_frequency: {
          per_transaction: "Per Transaction",
          weekly: "Weekly",
          monthly: "Monthly"
        },
        invoice_delivery: {
          email: "Email",
          mail: "Mail",
          portal: "Portal"
        },
        service_priority: {
          standard: "Standard",
          priority: "Priority",
          emergency: "Emergency"
        }
      }
    }
  },

  fr: {
    // Model names
    singular: "Client",
    plural: "Clients",

    // Tabs
    tabs: {
      general: "Général",
      client: "Détails du client",
      billing: "Facturation",
      tasks: "Tâches",
      key_management: "Gestion des clés",
      keys: "Clés",
      notices: "Avis",
      submissions: "Formulaires remplis",
      equipment_control_reports: "Contrôle d'équipement"
    },

    // Blocks
    blocks: {
      identity: "Identité",
      contact: "Contact",
      address: "Adresse",
      contact_methods: "Moyens de contact",
      classification: "Classification",
      financial: "Finances",
      preferences: "Préférences",
      transaction_settings: "Paramètres de transaction",
      client_info: "Informations client"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Identity fields
      code: "Code",
      name: "Nom",
      legal_name: "Raison sociale",
      type: "Type",
      status: "Statut",
      label: "Étiquette",
      website: "Site web",
      notes: "Notes",
      primaryAddress: {
        street_number: "Numéro civique",
        address: "Adresse",
        city: "Ville",
        zip: "Code postal",
        state: "Province / État",
        country: "Pays",
        full_address: "Adresse complète"
      },
      created_at: "Créé le",
      updated_at: "Modifié le",

      // Client-specific data (JSONB)
      client_data: {
        tier: "Niveau",
        client_status: "Statut client",
        source: "Source",
        credit_limit_cents: "Limite de crédit",
        payment_terms_days: "Délai de paiement (jours)",
        tax_exempt: "Exempté de taxes",
        tax_exempt_certificate: "Certificat d'exemption",
        account_manager_id: "Gestionnaire de compte",
        acquired_at: "Date d'acquisition",
        churned_at: "Date de résiliation",
        churn_reason: "Raison de résiliation",
        suspended_at: "Date de suspension",
        suspension_reason: "Raison de suspension",
        preferred_contact_method: "Méthode de contact préférée",
        billing_frequency: "Fréquence de facturation",
        invoice_delivery: "Mode de livraison des factures",
        service_priority: "Priorité de service"
      },

      // Transaction settings (JSONB)
      transaction_settings: {
        currency: "Devise",
        language: "Langue",
        default_discount: "Remise par défaut"
      }
    },

    // Badges
    badges: {
      // Status
      active: "Actif",
      inactive: "Inactif",

      // Client Status
      prospect: "Prospect",
      suspended: "Suspendu",
      churned: "Résilié",

      // Tier
      standard: "Standard",
      silver: "Argent",
      gold: "Or",
      platinum: "Platine",
      enterprise: "Entreprise",

      // Type
      company: "Entreprise",
      individual: "Particulier",
      government: "Gouvernement",
      nonprofit: "Organisme à but non lucratif",
      other: "Autre",

      // Boolean
      true: "Oui",
      false: "Non"
    },

    // Filters
    filters: {
      status: "Statut",
      "client_data->tier": "Niveau",
      "client_data->client_status": "Statut client",
      type: "Type"
    },

    // Options
    options: {
      type: {
        company: "Entreprise",
        individual: "Particulier",
        government: "Gouvernement",
        nonprofit: "Organisme à but non lucratif",
        other: "Autre"
      },
      client_data: {
        tier: {
          standard: "Standard",
          silver: "Argent",
          gold: "Or",
          platinum: "Platine",
          enterprise: "Entreprise"
        },
        client_status: {
          prospect: "Prospect",
          active: "Actif",
          suspended: "Suspendu",
          churned: "Résilié"
        },
        preferred_contact_method: {
          email: "Courriel",
          phone: "Téléphone",
          sms: "SMS"
        },
        billing_frequency: {
          per_transaction: "Par transaction",
          weekly: "Hebdomadaire",
          monthly: "Mensuel"
        },
        invoice_delivery: {
          email: "Courriel",
          mail: "Courrier",
          portal: "Portail"
        },
        service_priority: {
          standard: "Standard",
          priority: "Prioritaire",
          emergency: "Urgence"
        }
      }
    }
  }
};
